import React, { useState, useEffect, useMemo } from 'react';
import { Cpu, LayoutGrid } from 'lucide-react';
import { puntovService } from '../../../services/api';
import Modal from './Modal';
import PosicionesTipo from './PosicionesTipo';
import EditorPosiciones from './EditorPosiciones';

const VACIO = { id_maquina: '', tipo_maq: '', descripcion_maq: '' };
const OTRO_TIPO = '__otro__';
const claseInput = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none';

// Ventana de alta de máquina: datos a la izquierda, posiciones a la derecha
// (las del tipo elegido, o el editor si es un tipo propio).
const FormularioMaquina = ({ cliente, onCreado, onCancel }) => {
  const [form, setForm] = useState(VACIO);
  const [tipos, setTipos] = useState([]);
  const [otroTipo, setOtroTipo] = useState(false);
  const [posiciones, setPosiciones] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Tipos con sus posiciones, tal como los crea Punto V. "Otro tipo…" permite
  // uno que Punto V no conoce, con posiciones definidas aquí.
  useEffect(() => {
    puntovService.tiposMaquina()
      .then(res => setTipos(res.data || []))
      .catch(() => {});
  }, []);

  const cambiar = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));

  const elegirTipo = (valor) => {
    const esOtro = valor === OTRO_TIPO;
    setOtroTipo(esOtro);
    cambiar('tipo_maq', esOtro ? '' : valor);
  };

  const escribirTipo = otroTipo || tipos.length === 0;
  const tipoElegido = useMemo(() => tipos.find(t => t.tipo === form.tipo_maq) || null, [tipos, form.tipo_maq]);
  const tipoPropio = escribirTipo && !tipoElegido;

  const enviar = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await puntovService.crearMaquina({
        ...form,
        id_duenio: cliente.duenio.id,
        correo: cliente.duenio.correo,
        ...(tipoPropio && posiciones.length ? { posiciones } : {})
      });
      onCreado(res);
    } catch (err) {
      setError(err.message || 'Error al registrar la máquina');
      setLoading(false);
    }
  };

  return (
    <Modal
      titulo="Nueva máquina"
      subtitulo={`Cliente ${cliente.slug} · ${cliente.base_datos}`}
      icono={Cpu}
      onClose={onCancel}
      bloqueado={loading}
      ancho="xl"
      pie={(
        <>
          <button type="button" onClick={onCancel} disabled={loading} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">
            Cancelar
          </button>
          <button
            type="submit"
            form="form-nueva-maquina"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Registrando…' : 'Registrar máquina'}
          </button>
        </>
      )}
    >
      <form id="form-nueva-maquina" onSubmit={enviar} className="grid grid-cols-1 lg:grid-cols-[17rem_1fr] gap-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="maquina-id" className="block text-xs font-medium text-gray-700 mb-1">Id de máquina *</label>
            <input
              id="maquina-id"
              value={form.id_maquina}
              onChange={(e) => cambiar('id_maquina', e.target.value)}
              required
              autoFocus
              maxLength={50}
              pattern="[A-Za-z0-9_]+"
              title="Solo letras, números y guion bajo"
              placeholder="BIG_COMBO_1"
              className={`${claseInput} font-mono`}
            />
          </div>

          <div>
            <label htmlFor="maquina-tipo" className="block text-xs font-medium text-gray-700 mb-1">Tipo *</label>
            {tipos.length > 0 && (
              <select
                id="maquina-tipo"
                value={otroTipo ? OTRO_TIPO : form.tipo_maq}
                onChange={(e) => elegirTipo(e.target.value)}
                required={!otroTipo}
                className={`${claseInput} bg-white`}
              >
                <option value="">Seleccione el tipo…</option>
                {tipos.map(t => (
                  <option key={t.tipo} value={t.tipo}>{`${t.tipo} — ${t.posiciones.length} posiciones`}</option>
                ))}
                <option value={OTRO_TIPO}>Otro tipo…</option>
              </select>
            )}
            {escribirTipo && (
              <input
                aria-label="Nombre del tipo de máquina"
                value={form.tipo_maq}
                onChange={(e) => cambiar('tipo_maq', e.target.value.toUpperCase())}
                required
                autoFocus={otroTipo}
                pattern="[A-Za-z0-9_]+"
                title="Solo letras, números y guion bajo"
                placeholder="Nombre del tipo, ej. VITRINA_6"
                className={`${claseInput} font-mono ${tipos.length > 0 ? 'mt-2' : ''}`}
              />
            )}
          </div>

          <div>
            <label htmlFor="maquina-descripcion" className="block text-xs font-medium text-gray-700 mb-1">Descripción / ubicación *</label>
            <input
              id="maquina-descripcion"
              value={form.descripcion_maq}
              onChange={(e) => cambiar('descripcion_maq', e.target.value)}
              required
              placeholder="Lobby Torre Gaman"
              className={claseInput}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
          )}
        </div>

        <div className="min-w-0">
          {tipoElegido ? (
            <PosicionesTipo tipo={tipoElegido} />
          ) : tipoPropio ? (
            <>
              <EditorPosiciones value={posiciones} onChange={setPosiciones} tipos={tipos} />
              {posiciones.length === 0 && (
                <p className="text-xs text-amber-700 mt-2">Sin posiciones la máquina se crea vacía.</p>
              )}
            </>
          ) : (
            <div className="h-full min-h-[12rem] border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center p-6 text-gray-400">
              <LayoutGrid size={28} className="mb-2" />
              <p className="text-sm">Elija el tipo para ver sus posiciones,</p>
              <p className="text-sm">o "Otro tipo…" para definirlas.</p>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default FormularioMaquina;
